"use client";

import React, { useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { FaBars } from "react-icons/fa";
import { MdKeyboardArrowDown } from "react-icons/md";
import Drawer from "./Drawer";
import { useZocdocBooking } from "@/hooks/useZocDocBooking";

interface ServicesMenuProps {
  isOpen: boolean;
  pathname: string;
  closeMenu: () => void;
}

const ServicesMenu: React.FC<ServicesMenuProps> = ({
  isOpen,
  pathname,
  closeMenu,
}) => {
  const services = {
    General: [
      { display: "Dental Cleanings", route: "dental-cleanings" },
      { display: "Dental Sealants", route: "dental-sealants" },
      { display: "Emergency Treatment", route: "emergency-treatment" },
      { display: "Extractions", route: "extractions" },
      { display: "Fluoride Treatment", route: "fluoride-treatment" },
      { display: "Night Guards", route: "night-guards" },
      { display: "Oral Cancer Screening", route: "oral-cancer-screening" },
      { display: "Oral Exams", route: "oral-exams" },
      { display: "Pediatric Dentistry", route: "pediatric-dentistry" },
      { display: "Periodontal Treatment", route: "periodontal-treatment" },
      { display: "Preventative Program", route: "preventative-program" },
      { display: "Root Canals", route: "root-canals" },
      { display: "Sedation Dentistry", route: "sedation-dentistry" },
      { display: "Sleep Apnea", route: "sleep-apnea" },
      { display: "Snoring Appliances", route: "snoring-appliances" },
      { display: "Sports Mouthguards", route: "sports-mouthguards" },
      { display: "TMJ Disorders", route: "tmj-disorders" },
    ],
    Restorative: [
      { display: "All-on-4®", route: "all-on-4" },
      { display: "All-on-6®", route: "all-on-6" },
      { display: "CEREC", route: "cerec-single-visit-crowns" },
      { display: "Crowns & Caps", route: "crowns-and-caps" },
      { display: "Dental Bridges", route: "dental-bridges" },
      { display: "Dental Fillings", route: "dental-fillings" },
      { display: "Dentures", route: "dentures" },
      { display: "Full Mouth Reconstruction", route: "full-mouth-reconstruction" },
      { display: "Implant Dentistry", route: "implant-dentistry" },
      { display: "Implant Restorations", route: "implant-restorations" },
      { display: "Inlays & Onlays", route: "inlays-onlays" },
    ],
    Cosmetic: [
      { display: "Botox®", route: "botox" },
      { display: "Ceramic Crowns", route: "ceramic-crowns" },
      { display: "Cosmetic Dentistry", route: "cosmetic-dentistry" },
      { display: "Dermal Fillers", route: "dermal-fillers" },
      { display: "Juvederm®", route: "juvederm" },
      { display: "Opalescence® Whitening", route: "opalescence" },
      { display: "Teeth Whitening", route: "teeth-whitening" },
      { display: "Veneers", route: "veneers" },
    ],
    Technology: [
      { display: "Air Abrasion", route: "air-abrasion" },
      { display: "CBCT", route: "cbct" },
      { display: "Dental Lab", route: "dental-lab" },
      { display: "Digital Impressions", route: "digital-impressions" },
      { display: "Digital Radiography", route: "digital-radiography" },
      { display: "E4D", route: "e4d" },
      { display: "LANAP®", route: "lanap" },
      { display: "Laser Dentistry", route: "laser-dentistry" },
      { display: "VELscope® Cancer Screening", route: "velscope-cancer-screening" },
    ],
    Orthodontics: [
      { display: "Invisalign®", route: "invisalign" },
    ],
  };

  return (
    <div
      className={`absolute top-full left-1/2 transform -translate-x-1/2 bg-white shadow-2xl z-50 transition-all duration-300 ease-in-out rounded-lg border border-gray-100 ${
        isOpen
          ? "opacity-100 translate-y-0 visible"
          : "opacity-0 -translate-y-2 invisible"
      }`}
      style={{ width: 'min(90vw, 1000px)' }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-2xl font-bold text-gray-900 mb-2">Our Services</div>
          <p className="text-gray-600">Comprehensive dental care for your entire family</p>
        </div>
        
        {/* Services Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {Object.entries(services).map(([category, items]) => (
            <div key={category} className="space-y-3">
              <div className="text-lg font-semibold text-[#AE7D2F] border-b border-gray-200 pb-2">
                {category}
              </div>
              <ul className="space-y-2">
                {items.map((service) => {
                  const servicePath = `/services/${service.route}`;
                  const isActive = pathname === servicePath;
                  return (
                    <li key={service.route}>
                      <Link
                        href={servicePath}
                        className={`text-sm block py-1 px-2 rounded transition-all duration-200 ${
                          isActive
                            ? "bg-gradient-to-r from-[#AE7D2F] to-[#ECD05B] text-white"
                            : "text-gray-600 hover:text-[#AE7D2F] hover:bg-gray-50"
                        }`}
                        onClick={closeMenu}
                      >
                        {service.display}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Navbar: React.FC = () => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pathname = usePathname();
  const { openBooking } = useZocdocBooking();

  const handleMouseEnter = (menu: string | null) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpenMenu(menu);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenMenu(null);
    }, 150);
  };

  const handleHamburgerClick = () => {
    console.log("Hamburger clicked, isDrawerOpen:", isDrawerOpen);
    setIsDrawerOpen(true);
  };

  const closeMenu = () => {
    setOpenMenu(null);
  };

  const aboutUsPaths = [
    "/our-office",
    "/our-team",
    "/testimonials",
    "/our-videos",
    "/our-community",
    "/local-communities",
    "/faq",
  ];
  const patientInfoPaths = ["/new-patients", "/library"];

  // Generate service paths from the services object
  const servicePaths = Object.values({
    General: [
      { display: "Dental Cleanings", route: "dental-cleanings" },
      { display: "Dental Sealants", route: "dental-sealants" },
      { display: "Emergency Treatment", route: "emergency-treatment" },
      { display: "Extractions", route: "extractions" },
      { display: "Fluoride Treatment", route: "fluoride-treatment" },
      { display: "Night Guards", route: "night-guards" },
      { display: "Oral Cancer Screening", route: "oral-cancer-screening" },
      { display: "Oral Exams", route: "oral-exams" },
      { display: "Pediatric Dentistry", route: "pediatric-dentistry" },
      { display: "Periodontal Treatment", route: "periodontal-treatment" },
      { display: "Preventative Program", route: "preventative-program" },
      { display: "Root Canals", route: "root-canals" },
      { display: "Sedation Dentistry", route: "sedation-dentistry" },
      { display: "Sleep Apnea", route: "sleep-apnea" },
      { display: "Snoring Appliances", route: "snoring-appliances" },
      { display: "Sports Mouthguards", route: "sports-mouthguards" },
      { display: "TMJ Disorders", route: "tmj-disorders" },
    ],
    Restorative: [
      { display: "All-on-4®", route: "all-on-4" },
      { display: "All-on-6®", route: "all-on-6" },
      { display: "CEREC", route: "cerec-single-visit-crowns" },
      { display: "Crowns & Caps", route: "crowns-and-caps" },
      { display: "Dental Bridges", route: "dental-bridges" },
      { display: "Dental Fillings", route: "dental-fillings" },
      { display: "Dentures", route: "dentures" },
      { display: "Full Mouth Reconstruction", route: "full-mouth-reconstruction" },
      { display: "Implant Dentistry", route: "implant-dentistry" },
      { display: "Implant Restorations", route: "implant-restorations" },
      { display: "Inlays & Onlays", route: "inlays-onlays" },
    ],
    Cosmetic: [
      { display: "Botox®", route: "botox" },
      { display: "Ceramic Crowns", route: "ceramic-crowns" },
      { display: "Cosmetic Dentistry", route: "cosmetic-dentistry" },
      { display: "Dermal Fillers", route: "dermal-fillers" },
      { display: "Juvederm®", route: "juvederm" },
      { display: "Opalescence® Whitening", route: "opalescence" },
      { display: "Teeth Whitening", route: "teeth-whitening" },
      { display: "Veneers", route: "veneers" },
    ],
    Technology: [
      { display: "Air Abrasion", route: "air-abrasion" },
      { display: "CBCT", route: "cbct" },
      { display: "Dental Lab", route: "dental-lab" },
      { display: "Digital Impressions", route: "digital-impressions" },
      { display: "Digital Radiography", route: "digital-radiography" },
      { display: "E4D", route: "e4d" },
      { display: "LANAP®", route: "lanap" },
      { display: "Laser Dentistry", route: "laser-dentistry" },
      { display: "VELscope® Cancer Screening", route: "velscope-cancer-screening" },
    ],
    Orthodontics: [
      { display: "Invisalign®", route: "invisalign" },
    ],
  })
    .flat()
    .map((service) => `/services/${service.route}`);

  const isAboutUsActive = aboutUsPaths.includes(pathname);
  const isPatientInfoActive = patientInfoPaths.includes(pathname);
  const isServicesActive = servicePaths.includes(pathname);

  return (
    <div className="w-full">
      {/* Header bar with action buttons */}
      <div className="bg-white fixed top-0 left-0 right-0 z-40 py-1 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-end space-x-2">
            <a
              href="tel:+1234567890"
              className="bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] text-white text-xs font-bold py-1 px-2 rounded shadow-lg transition-all duration-300 ease-in-out tracking-wide [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)]"
            >
              CALL US
            </a>
            <button
              onClick={openBooking}
              className="bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B] text-white text-xs font-bold py-1 px-2 rounded shadow-lg transition-all duration-300 ease-in-out tracking-wide [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)] cursor-pointer"
            >
              BOOK ONLINE
            </button>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="bg-white shadow-md fixed top-7 left-0 right-0 z-30 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link href="/">
                <Image
                  src="/images/custom_images_logo.webp"
                  alt="Logo"
                  width={168}
                  height={56}
                  className="h-10 sm:h-14 w-auto"
                />
              </Link>
            </div>

            {/* Right side container with navigation */}
            <div className="flex items-center">
              {/* Desktop Navigation */}
              <div className="hidden xl:flex items-center">
                <div className="xl:ml-6 xl:flex xl:space-x-4">
                  <Link
                    href="/"
                    className={`px-3 py-2 text-lg font-medium ${
                      pathname === "/"
                        ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                        : "text-gray-900"
                    } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                    onMouseEnter={() => handleMouseEnter(null)}
                  >
                    Home
                  </Link>
                  <Link
                    href="/meet-your-dentist"
                    className={`px-4 py-2 text-lg font-medium ${
                      pathname === "/meet-your-dentist"
                        ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                        : "text-gray-900"
                    } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                    onMouseEnter={() => handleMouseEnter(null)}
                  >
                    Meet Dr. Khaimov
                  </Link>
                  <div
                    className="relative"
                    onMouseEnter={() => handleMouseEnter("about-us")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`px-4 py-2 text-lg font-medium focus:outline-none cursor-pointer flex items-center gap-1 ${
                        isAboutUsActive
                          ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                          : "text-gray-900"
                      } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                    >
                      About Us
                      <MdKeyboardArrowDown className={`text-2xl text-black transition-transform duration-200 ${openMenu === "about-us" ? "rotate-180" : ""}`} />
                    </button>
                    {openMenu === "about-us" && (
                      <div
                        className={`absolute top-full left-0 bg-white shadow-lg rounded-md z-50 min-w-[200px] transition-all duration-300 ease-in-out ${
                          openMenu === "about-us"
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-4 pointer-events-none"
                        }`}
                      >
                        <ul className="py-2">
                          {[
                            { href: "/our-office", label: "Our Office" },
                            { href: "/our-team", label: "Our Team" },
                            { href: "/testimonials", label: "Testimonials" },
                            { href: "/our-videos", label: "Our Videos" },
                            { href: "/our-community", label: "Our Community" },
                            {
                              href: "/local-communities",
                              label: "Local Communities",
                            },
                            { href: "/faq", label: "FAQ" },
                          ].map(({ href, label }) => (
                            <li key={href}>
                              <Link
                                href={href}
                                className={`block px-4 py-2 text-lg ${
                                  pathname === href
                                    ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                                    : "text-gray-600"
                                } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                                onClick={closeMenu}
                              >
                                {label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <Link
                    href="/blog"
                    className={`px-3 py-2 text-lg font-medium ${
                      pathname === "/blog"
                        ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                        : "text-gray-900"
                    } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                    onMouseEnter={() => handleMouseEnter(null)}
                  >
                    Blog
                  </Link>
                  <div
                    className="relative"
                    onMouseEnter={() => handleMouseEnter("services")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`px-3 py-2 text-lg font-medium focus:outline-none cursor-pointer flex items-center gap-1 ${
                        isServicesActive
                          ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                          : "text-gray-900"
                      } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                    >
                      Services
                      <MdKeyboardArrowDown className={`text-2xl text-black transition-transform duration-200 ${openMenu === "services" ? "rotate-180" : ""}`} />
                    </button>
                    <ServicesMenu
                      isOpen={openMenu === "services"}
                      pathname={pathname}
                      closeMenu={closeMenu}
                    />
                  </div>
                  <div
                    className="relative"
                    onMouseEnter={() => handleMouseEnter("patient-info")}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className={`px-3 py-2 text-lg font-medium focus:outline-none cursor-pointer flex items-center gap-1 ${
                        isPatientInfoActive
                          ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                          : "text-gray-900"
                      } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                    >
                      Patient Info
                      <MdKeyboardArrowDown className={`text-2xl text-black transition-transform duration-200 ${openMenu === "patient-info" ? "rotate-180" : ""}`} />
                    </button>
                    {openMenu === "patient-info" && (
                      <div
                        className={`absolute top-full left-0 bg-white shadow-lg rounded-md z-50 min-w-[200px] transition-all duration-300 ease-in-out ${
                          openMenu === "patient-info"
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-4 pointer-events-none"
                        }`}
                      >
                        <ul className="py-2">
                          {[
                            { href: "/new-patients", label: "New Patients" },
                            { href: "/library", label: "Dental Health" },
                          ].map(({ href, label }) => (
                            <li key={href}>
                              <Link
                                href={href}
                                className={`block px-4 py-2 text-lg ${
                                  pathname === href
                                    ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                                    : "text-gray-600"
                                } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                                onClick={closeMenu}
                              >
                                {label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <Link
                    href="/contact"
                    className={`px-3 py-2 text-lg font-medium ${
                      pathname === "/contact"
                        ? "text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]"
                        : "text-gray-900"
                    } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                    onMouseEnter={() => handleMouseEnter(null)}
                  >
                    Contact
                  </Link>
                </div>
              </div>

              {/* Hamburger Icon for Mobile */}
              <button
                className="xl:hidden text-2xl text-gray-900"
                onClick={handleHamburgerClick}
                aria-label="Open menu"
              >
                <FaBars />
              </button>
            </div>
          </div>
        </div>
      </nav>
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => {
          console.log("Closing drawer");
          setIsDrawerOpen(false);
        }}
      />
    </div>
  );
};

export default Navbar;